import Yup from '~/util/yup';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { DischargeLocationEnum, CareOfOptions, CareOfEnum, DischargeLocationOptions } from '@generated';
import { AutocompleteSearchFormField } from '~/shared/forms/controls/fields/AutocompleteSearchFormField';
import { CheckboxFormField } from '~/shared/forms/controls/fields/CheckboxFormField';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { PatientContactSelectField } from '~/features/demographics/contacts/controls/PatientContactSelectField';
import { useForm } from '~/shared/forms/FormContext';

export const validationSchema = Yup.object().shape({
	patientDischargeGoal: Yup.string().label('Patient Discharge Goal').nullable().required(),
	noPlannedDischarge: Yup.boolean().label('No Planned Discharge').required(),
	reasonForNoPlannedDischarge: Yup.string()
		.label('Reason for No Planned Discharge')
		.nullable()
		.when(['noPlannedDischarge'], {
			is: true,
			then: schema => schema.required(),
			otherwise: schema => schema
		}),
	careOfId: Yup.string()
		.label('Discharge To Care Of')
		.nullable()
		.when(['noPlannedDischarge'], {
			is: false,
			then: schema => schema.required(),
			otherwise: schema => schema
		}),
	dischargeToCaregiverId: Yup.string()
		.nullable()
		.label('Caregiver to discharge to')
		.when(['dischargeToCareOfId'], {
			is: CareOfEnum.Caregiver,
			then: schema => schema.required(),
			otherwise: schema => schema
		}),
	dischargeToPhysicianId: Yup.string()
		.nullable()
		.label('Physician to discharge to')
		.when(['dischargeToCareOfId'], {
			is: CareOfEnum.Physician,
			then: schema => schema.required(),
			otherwise: schema => schema
		}),
	dischargeLocationId: Yup.string()
		.label('Discharge Location')
		.nullable()
		.when(['noPlannedDischarge'], {
			is: false,
			then: schema => schema.required(),
			otherwise: schema => schema
		}),
	dischargeToFacilityId: Yup.string()
		.label('Facility')
		.nullable()
		.when(['dischargeLocationId'], {
			is: DischargeLocationEnum.Facility,
			then: schema => schema.required('Facility is required'),
			otherwise: schema => schema
		}),
	otherLocation: Yup.string()
		.label('Other Location')
		.nullable()
		.when(['dischargeLocationId'], {
			is: DischargeLocationEnum.Other,
			then: schema => schema.required('Other Location is required'),
			otherwise: schema => schema
		}),
	dischargePlanDiscussedWithPatient: Yup.boolean().label('Discharge Plan Discussed with Patient').required(),
	notes: Yup.string().label('Additional Notes').nullable()
});

export const initialValues = {
	patientDischargeGoal: null,
	noPlannedDischarge: false,
	reasonForNoPlannedDischarge: null,
	careOfId: null,
	dischargeToCaregiverId: null,
	dischargeToPhysicianId: null,
	dischargeLocationId: null,
	otherLocation: null,
	dischargeToFacilityId: null,
	dischargePlanDiscussedWithPatient: false,
	notes: null
};

export function DischargePlanFormBody() {
	const { formHelpers } = useForm();
	const careOfValue = formHelpers.values.careOfId ? Number(formHelpers.values.careOfId) : null;
	const dischargeLocationValue = formHelpers.values.dischargeLocationId ? Number(formHelpers.values.dischargeLocationId) : null;
	return (
		<div className="grid grid-cols-1 gap-8">
			<div className="grid grid-cols-1 gap-6">
				<CheckboxFormField name="noPlannedDischarge" hideLabel />
				{formHelpers.values.noPlannedDischarge ? (
					<TextAreaFormField name="reasonForNoPlannedDischarge" />
				) : (
					<>
						<div className="grid grid-cols-2 gap-2">
							<SelectFormField name="careOfId" options={CareOfOptions} />
							{careOfValue === CareOfEnum.Caregiver && (
								<PatientContactSelectField stayId={formHelpers.values.stayId} name="dischargeToCaregiverId" />
							)}
							{careOfValue === CareOfEnum.Physician && (
								<AutocompleteSearchFormField name="dischargeToPhysicianId" searchType="physician" />
							)}
						</div>
						<div className="grid grid-cols-2 gap-2">
							<SelectFormField name="dischargeLocationId" options={DischargeLocationOptions} />
							{dischargeLocationValue === DischargeLocationEnum.Facility && (
								<AutocompleteSearchFormField name="dischargeToFacilityId" searchType="facility" />
							)}
							{dischargeLocationValue === DischargeLocationEnum.Other && <TextFormField name="otherLocation" />}
						</div>

						<CheckboxFormField name="dischargePlanDiscussedWithPatient" hideLabel />
					</>
				)}
				<TextAreaFormField name="patientDischargeGoal" />
				<TextAreaFormField name="notes" />
			</div>
		</div>
	);
}
